/**
 * Pure geometric utility functions for arrow calculations
 *
 * These functions are stateless and operate only on vectors/positions.
 * They are separated from the main arrow logic for clarity and testability.
 */

import { v3 } from "@alchemiesolns/maths";

/**
 * Finds the direction from available directions that is closest to opposite
 * the direction toward a reference point.
 *
 * This is used to select the best lone pair direction that points away from
 * the destination atom.
 *
 * @param directions - Available direction vectors (e.g., lone pair orientations)
 * @param point - The starting point (e.g., atom position)
 * @param referencePoint - The target point to point away from (e.g., destination)
 * @returns The direction that is most opposite to the reference direction
 */
export const findClosestOppositeVector = (
  directions: number[][],
  point: number[],
  referencePoint: number[]
): number[] => {
  // Get direction FROM point TO referencePoint
  const directionToRef = v3.normalize(v3.subVectors(referencePoint, point));
  const opposite = v3.negate(directionToRef);

  let closest: number[] | undefined;
  let smallestDot = Infinity;

  for (const dir of directions) {
    const dot = v3.dotVectors(dir, opposite);
    if (dot < smallestDot) {
      smallestDot = dot;
      closest = dir;
    }
  }

  return closest ? closest : [0, 0, 0];
};

/**
 * Calculates the midpoint between two positions.
 * Common for "fish" arrows that terminate halfway.
 */
export const calculateMidpoint = (start: number[], end: number[]): number[] => {
  return v3.midpoint(start, end);
};

/**
 * Calculates a bond direction perpendicular to the bond axis,
 * choosing the side closer to an opposite position.
 *
 * @param bondDir - Bond direction vector
 * @param bondPosition - Position along the bond
 * @param oppPos - Opposite position (e.g., arrow start)
 * @param camDir - Camera direction for cross product
 * @returns Perpendicular direction pointing toward the correct side
 */
export const calculateBondPerpendicularDirection = (
  bondDir: number[],
  bondPosition: number[],
  oppPos: number[],
  camDir: number[],
  offset: number = 1.2
): number[] => {
  const perp = v3.normalize(v3.crossVectors(camDir, bondDir));
  const vect = v3.scale(perp, offset);
  const pPos = v3.subVectors(bondPosition, vect);
  const pNeg = v3.addVectors(bondPosition, vect);

  if (v3.distance(pPos, oppPos) < v3.distance(pNeg, oppPos)) {
    return v3.negate(perp);
  } else {
    return perp;
  }
};

/**
 * Calculates direction from one particle to another
 */
export const calculateParticleDirection = (
  pos: number[],
  oppPos: number[]
): number[] => {
  return v3.normalize(v3.subVectors(oppPos, pos));
};

/**
 * Blends two direction vectors with a given weight
 *
 * @param dir1 - First direction vector
 * @param dir2 - Second direction vector
 * @param weight - Blending weight (0.5 = equal blend)
 * @returns Normalized blended direction
 */
export const blendDirections = (
  dir1: number[],
  dir2: number[],
  weight: number = 0.5
): number[] => {
  return v3.normalize(v3.addScaledVector(dir1, dir2, weight));
};

/**
 * Calculates a control point for Bezier curves
 *
 * @param position - Base position
 * @param direction - Direction vector from base
 * @param distance - Distance multiplier
 * @returns Control point position
 */
export const calculateControlPoint = (
  position: number[],
  direction: number[],
  distance: number,
  scalar: number = 0.5
): number[] => {
  return v3.addVectors(position, v3.scale(direction, scalar * distance));
};

/**
 * Extends a point along a direction by a given offset
 */
export const extendPoint = (
  position: number[],
  direction: number[],
  offset: number
): number[] => {
  return v3.addScaledVector(position, direction, offset);
};

/**
 * Determines which side of a line a start position is on,
 * useful for arrow head positioning
 *
 * @param headPos - Arrow head position
 * @param perpDir - Perpendicular direction to the line
 * @param startPos - Start position to compare
 * @param offset - Offset distance for side checking
 * @returns true if left side, false if right side
 */
export const determineLeftSide = (
  headPos: number[],
  perpDir: number[],
  startPos: number[],
  offset: number = 1
): boolean => {
  const h1 = v3.addScaledVector(headPos, perpDir, offset);
  const h2 = v3.addScaledVector(headPos, perpDir, -offset);
  return v3.distance(h1, startPos) >= v3.distance(h2, startPos);
};
